import { StatName } from "@/shared/const/glossary";
import { theme } from "@/shared/theme";
import { PlayerMetadataRow } from "@suns/api/generated-client/apollo";
import {
  Flex,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from "@suns/design-system";
import { ChartConfig } from "@suns/design-system/src/components/Chart/Chart";

import { MetricButton } from "./MetricButton";
import { DataAreaChart } from "@/components";
import {
  formatMoney,
  formatPlayerChartData,
} from "@/shared/utils/helper-functions";

const chartConfig = {
  prv: {
    label: StatName.PRV,
    color: theme.colors.purple.DEFAULT,
  },
} satisfies ChartConfig;

const tooltipConfig = {
  prv: {
    formatter: (value: number) => formatMoney(value),
    relatedMetrics: [
      { key: "ppv", label: "PPV" },
      { key: "capHit", label: "Cap Hit" },
    ],
  },
};

export function PlayerPrvChart({
  playerMetadata,
}: {
  playerMetadata: PlayerMetadataRow[];
}) {
  const chartDataMonthly = formatPlayerChartData(
    "monthly",
    playerMetadata.filter((data) => data.prv !== null)
  );
  const chartDataAnnual = formatPlayerChartData(
    "annual",
    playerMetadata.filter((data) => data.prv !== null)
  );

  type MetricKey = keyof typeof chartConfig;

  const getMetricValues = (
    data: typeof chartDataAnnual,
    metric: MetricKey
  ) => ({
    currentValue: data[data.length - 1]?.[metric]?.toString() ?? null,
    previousValue:
      data.find((d) => d[metric] !== null)?.[metric]?.toString() ?? null,
  });

  return (
    <Flex direction="down" gap="md" className="w-full">
      <Tabs defaultValue="annual">
        <Flex direction="right" justify="between">
          <Text heading muted size="lg">
            Player Relative Value ({StatName.PRV})
          </Text>
          <TabsList>
            <TabsTrigger value="annual">Annual</TabsTrigger>
            <TabsTrigger value="monthly">Monthly</TabsTrigger>
          </TabsList>
        </Flex>
        <TabsContent value="annual">
          <Flex direction="down" gap="sm" className="w-full">
            <Flex direction="right" gap="sm" wrap>
              <MetricButton
                key="prv"
                label={StatName.PRV}
                color={theme.colors.gray[500]}
                currentValue={
                  getMetricValues(chartDataAnnual, "prv").currentValue
                }
                differenceType="percentage"
                disabled={true}
              />
            </Flex>
            <DataAreaChart
              dateRange="annual"
              chartData={chartDataAnnual.map((data) => ({
                ...data,
                snapshotDate: data.snapshotDate ?? "",
              }))}
              chartConfig={chartConfig}
              tooltipConfig={tooltipConfig}
            />
          </Flex>
        </TabsContent>
        <TabsContent value="monthly">
          <Flex direction="down" gap="sm" className="w-full">
            <Flex direction="right" gap="sm" wrap>
              <MetricButton
                key="prv"
                label={StatName.PRV}
                color={theme.colors.gray[500]}
                currentValue={
                  getMetricValues(chartDataAnnual, "prv").currentValue
                }
                differenceType="percentage"
                disabled={true}
              />
            </Flex>

            <DataAreaChart
              dateRange="monthly"
              chartData={chartDataMonthly.map((data) => ({
                ...data,
                snapshotDate: data.snapshotDate ?? "",
              }))}
              chartConfig={chartConfig}
              tooltipConfig={tooltipConfig}
            />
          </Flex>
        </TabsContent>
      </Tabs>
    </Flex>
  );
}
